import middleware from "../Api/Middleware";
import { Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import logo from './Logo-Unified-smb.png'
import { formatCurrency } from "../../utils";

// PDF Styles
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        padding: 35,
        fontFamily: 'Helvetica',
    },
    logo: {
        maxWidth: 200,
        maxHeight: 120,
        marginBottom: 20,
    },
    header: {
        fontSize: 24,
        marginBottom: 15,
        fontWeight: 'bold',
        color: '#333333',
    },
    companyInfo: {
        position: 'absolute',
        top: 50,
        right: 30,
        width: 200,
        fontSize: 9,
        color: 'black',
        textAlign: 'right',
        alignItems: 'flex-end'
    },
    invoiceDetails: {
        marginTop: 40,
        marginBottom: 20,
        fontSize: 12,
    },
    customerInfo: {
        marginBottom: 20,
        fontSize: 9,
    },
    tableHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 10,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        minWidth: 150,
    },
    headerCol: {
        flexDirection: 'column',
    },
    headerLabel: {
        fontWeight: 'bold',
        fontSize: 9,
        marginRight: 2,
        marginBottom: 2,
    },
    headerValue: {
        fontSize: 9,
        marginBottom: 2,
    },
    table: {
        display: 'table',
        width: 'auto',
        marginBottom: 20,
    },
    tableRowHeader: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#6AC1E7',
        paddingBottom: 5,
        marginBottom: 10,
    },
    tableRow: {
        flexDirection: 'row',
        paddingVertical: 1,
        minHeight: 30,
    },
    tableColHeader: {
        width: '25%',
        paddingHorizontal: 0,
        paddingVertical: 8,
        alignItems: 'flex-start',
    },
    tableCol: {
        paddingHorizontal: 0,
        paddingVertical: 8,
        alignItems: 'flex-start',
    },
    tableColRight: {
        paddingHorizontal: 0,
        paddingVertical: 8,
        alignItems: 'flex-end',
    },
    tableCellHeader: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#333333',
        textAlign: 'left',
    },
    tableCell: {
        fontSize: 9,
        color: '#333333',
        textAlign: 'left',
    },
    productDescription: {
        fontSize: 8,
        color: '#666666',
        textAlign: 'left',
        marginTop: 2,
        lineHeight: 1.2,
        paddingRight: 10,
    },
    tableColHeaderRight: {
        paddingHorizontal: 0,
        paddingVertical: 8,
        alignItems: 'flex-end',
    },
    tableCellHeaderRight: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#333333',
        textAlign: 'right',
    },
    tableCellRight: {
        fontSize: 9,
        color: '#333333',
        textAlign: 'right',
    },
    tableCellCenter: {
        fontSize: 9,
        color: '#333333',
        textAlign: 'center',
    },
    totals: {
        position: 'absolute',
        bottom: 100,
        right: 30,
        borderTopWidth: 1,
        borderTopStyle: 'solid',
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        paddingTop: 5,
        borderColor: '#6AC1E7',
        alignItems: 'flex-end',
    },
    totalRowBegin: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: 200,
        marginBottom: 5,
        fontSize: 9,
        fontWeight: 'bold',
        borderBottomWidth: 1,
        borderTopStyle: 'solid',
        borderColor: '#6AC1E7',
        paddingBottom: 5,
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: 200,
        marginBottom: 5,
        fontSize: 9,
    },
    totalRowFinal: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: 200,
        marginBottom: 5,
        fontSize: 9,
        fontWeight: 'bold',
        borderTopWidth: 1,
        borderTopStyle: 'solid',
        borderColor: '#6AC1E7',
        paddingTop: 5,
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 30,
        right: 30,
        fontSize: 9,
        color: 'black',
        textAlign: 'center',
    },
    discountPrice: {
        textDecoration: 'line-through',
    },
    continuationHeader: {
        marginBottom: 20,
    },
    pageSubtotal: {
        position: 'absolute',
        bottom: 60,
        right: 30,
        alignItems: 'flex-end',
        borderTopWidth: 1,
        borderTopStyle: 'solid',
        borderColor: '#6AC1E7',
        paddingTop: 5,
    },
    subtotalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: 200,
        fontSize: 9,
        fontWeight: 'bold',
        marginTop: 5,
    },
    creditNoteHeader: {
        fontSize: 24,
        marginBottom: 15,
        fontWeight: 'bold',
    },
});

// Helper function to parse currency string to number
const parseCurrencyValue = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    // Remove currency symbol and convert to number
    const cleanValue = value.toString().replace(/[€\s.]/g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
};

const InvoicePDFPages = ({ 
    documentType = 'FACTUUR', // 'FACTUUR' or 'CREDIT'
    documentNumber,
    documentDate,
    customer,
    company,
    deliveryAddress,
    invoiceAddress,
    productLines, // Array of preprocessed product lines
    totals, // Object with subtotal, btw, total
    footerText,
}) => {
    const companyLogo = company.logo ? `${middleware.defaults.imagesBaseURL}${company.logo}` : logo;
    const companyAddress = `${company.invoice_streetname || ''} ${company.invoice_house_number || ''}${company.invoice_addition ? ` - ${company.invoice_addition}` : ''}`.trim();
    const deliveryAddressStr = `${deliveryAddress?.streetname || ''} ${deliveryAddress?.housenumber || ''}${deliveryAddress?.addition ? ` - ${deliveryAddress?.addition}` : ''}`.trim();
    const invoiceAddressStr = `${invoiceAddress?.streetname || ''} ${invoiceAddress?.housenumber || ''}${invoiceAddress?.addition ? ` - ${invoiceAddress?.addition}` : ''}`.trim();

    // Pagination logic
    const PAGE_HEIGHT = 842;
    const MARGIN_TOP = 35;
    const MARGIN_BOTTOM = 35;
    const FOOTER_HEIGHT = 30;
    const SUBTOTAL_HEIGHT = 80;
    const FINAL_TOTALS_HEIGHT = 120;
    const FIRST_PAGE_HEADER_HEIGHT = 220;
    const CONTINUATION_HEADER_HEIGHT = 120;
    const TABLE_HEADER_HEIGHT = 35;
    const BASE_ROW_HEIGHT = 30;
    const DESCRIPTION_LINE_HEIGHT = 12;
    const CHARS_PER_LINE = 80;

    const estimateRowHeight = (item) => {
        let height = BASE_ROW_HEIGHT;
        if (item.description) {
            const descLength = item.description.length;
            const estimatedLines = Math.ceil(descLength / CHARS_PER_LINE);
            height += estimatedLines * DESCRIPTION_LINE_HEIGHT;
        }
        return height;
    };

    // Split items into pages and calculate page subtotals
    const pages = [];
    const pageSubtotals = []; // Array to store subtotals for each page
    let currentPageItems = [];
    let currentPageHeight = 0;
    let currentPageSubtotal = 0; // Track subtotal for current page
    let isFirstPage = true;

    productLines.forEach((item, index) => {
        const itemHeight = estimateRowHeight(item);
        
        let availableHeight;
        if (isFirstPage) {
            availableHeight = PAGE_HEIGHT - MARGIN_TOP - MARGIN_BOTTOM - FOOTER_HEIGHT - 
                            FIRST_PAGE_HEADER_HEIGHT - TABLE_HEADER_HEIGHT;
            if (index < productLines.length - 1) {
                availableHeight -= SUBTOTAL_HEIGHT;
            }
        } else {
            availableHeight = PAGE_HEIGHT - MARGIN_TOP - MARGIN_BOTTOM - FOOTER_HEIGHT - 
                            CONTINUATION_HEADER_HEIGHT - TABLE_HEADER_HEIGHT;
            if (index < productLines.length - 1) {
                availableHeight -= SUBTOTAL_HEIGHT;
            }
        }
        
        const isLastItem = index === productLines.length - 1;
        if (isLastItem && currentPageItems.length > 0) {
            availableHeight -= FINAL_TOTALS_HEIGHT;
        }
        
        if (currentPageHeight + itemHeight > availableHeight && currentPageItems.length > 0) {
            // Save current page and its subtotal
            pages.push(currentPageItems);
            pageSubtotals.push(currentPageSubtotal);
            
            // Reset for new page
            currentPageItems = [];
            currentPageHeight = 0;
            currentPageSubtotal = 0;
            isFirstPage = false;
        }
        
        currentPageItems.push(item);
        currentPageHeight += itemHeight;
        
        // Add to page subtotal (only items with totalPrice)
        if (item.totalPrice) {
            currentPageSubtotal += parseCurrencyValue(item.totalPrice);
        }
    });

    // Add final page and its subtotal
    if (currentPageItems.length > 0) {
        pages.push(currentPageItems);
        pageSubtotals.push(currentPageSubtotal);
    }

    // Table header component
    const TableHeader = () => (
        <View style={styles.tableRowHeader}>
            <View style={[styles.tableColHeader, { width: '15%' }]}>
                <Text style={styles.tableCellHeader}>Aantal</Text>
            </View>
            <View style={[styles.tableColHeader, { width: '45%' }]}>
                <Text style={styles.tableCellHeader}>Omschrijving</Text>
            </View>
            <View style={[styles.tableColHeaderRight, { width: '20%' }]}>
                <Text style={styles.tableCellHeaderRight}>Prijs per stuk</Text>
            </View>
            <View style={[styles.tableColHeaderRight, { width: '20%' }]}>
                <Text style={styles.tableCellHeaderRight}>Bedrag</Text>
            </View>
        </View>
    );

    // Product row component
    const ProductRow = ({ item }) => (
        <View style={styles.tableRow}>
            <View style={[styles.tableCol, { paddingLeft: 12, width: '15%' }]}>
                <Text style={styles.tableCell}>{item.quantity || ''}</Text>
            </View>
            <View style={[styles.tableCol, { width: '45%' }]}>
                <Text style={styles.tableCell}>{item.name}</Text>
                {item.description && (
                    <Text style={styles.productDescription}>{item.description}</Text>
                )}
            </View>
            <View style={[styles.tableColRight, { width: '20%' }]}>
                {item.showOriginalPrice && item.originalPrice ? (
                    <>
                        <Text style={[styles.tableCellRight, styles.discountPrice]}>{item.originalPrice}</Text>
                        <Text style={styles.tableCellRight}>{item.unitPrice}</Text>
                    </>
                ) : (
                    <Text style={styles.tableCellRight}>{item.unitPrice || ''}</Text>
                )}
            </View>
            <View style={[styles.tableColRight, { width: '20%' }]}>
                <Text style={styles.tableCellRight}>{item.totalPrice || ''}</Text>
            </View>
        </View>
    );

    return (
        <>
            {/* First Page */}
            <Page size="A4" style={styles.page}>
                {/* Company Info */}
                <View style={styles.companyInfo}>
                    <Text>{company.company_name}</Text>
                    <Text>{companyAddress}</Text>
                    <Text>{company.invoice_postalcode}, {company.invoice_city}</Text>
                    <Text> </Text>
                    <Text>{company.invoicemail}</Text>
                    <Text>{company.website}</Text>
                    <Text> </Text>
                    <Text>KVK: {company.cocnumber}</Text>
                    <Text>BTW: {company.vatnumber}</Text>
                    <Text>BANK: {company.iban}</Text>
                </View>

                {/* Logo */}
                <View style={{ minHeight: 100 }}>
                    <Image src={companyLogo} style={styles.logo} />
                </View>

                {/* Document Header */}
                <View>
                    <Text style={[styles.header, { color: '#333333' }]}>{documentType}</Text>
                </View>

                {/* Addresses and Details */}
                <View style={styles.tableHeader}>
                    {/* Invoice Address */}
                    <View style={styles.customerInfo}>
                        <Text style={styles.headerLabel}>Factuuradres:</Text>
                        {!invoiceAddress ? (
                            <Text>Geen factuuradres gevonden</Text>
                        ) : (
                            <>
                                {customer.company_name ? (
                                    <Text>{customer.company_name}</Text>
                                ) : (
                                    <Text>{customer.first_name} {customer.last_name}</Text>
                                )}
                                <Text>{invoiceAddressStr}</Text>
                                <Text>{invoiceAddress?.postalcode}, {invoiceAddress?.city}</Text>
                                <Text>{invoiceAddress?.country}</Text>
                            </>
                        )}
                    </View>

                    {/* Delivery Address */}
                    <View style={styles.customerInfo}>
                        <Text style={styles.headerLabel}>Bezorgadres:</Text>
                        {!deliveryAddress ? (
                            <Text>Geen bezorgadres gevonden</Text>
                        ) : (
                            <>
                                {customer.company_name ? (
                                    <Text>{customer.company_name}</Text>
                                ) : (
                                    <Text>{customer.first_name} {customer.last_name}</Text>
                                )}
                                <Text>{deliveryAddressStr}</Text>
                                <Text>{deliveryAddress?.postalcode}, {deliveryAddress?.city}</Text>
                                <Text>{deliveryAddress?.country}</Text>
                            </>
                        )}
                    </View>

                    {/* Document Details */}
                    <View style={styles.headerRow}>
                        <View style={styles.headerCol}>
                            <Text style={styles.headerLabel}>Klantnummer:</Text>
                            <Text style={styles.headerLabel}>Factuurnummer:</Text>
                            <Text style={styles.headerLabel}>Factuurdatum:</Text>
                        </View>
                        <View style={styles.headerCol}>
                            <Text style={styles.headerValue}>{customer?.internal_id || 'C-10000'}</Text>
                            <Text style={styles.headerValue}>{documentNumber}</Text>
                            <Text style={styles.headerValue}>{documentDate}</Text>
                        </View>
                    </View>
                </View>

                {/* Products Table */}
                <View style={styles.table}>
                    <TableHeader />
                    {pages[0] && pages[0].map((item, index) => (
                        <ProductRow key={index} item={item} />
                    ))}
                </View>

                {/* Page subtotal if multiple pages */}
                {pages.length > 1 && (
                    <View style={styles.pageSubtotal}>
                        <View style={styles.subtotalRow}>
                            <Text>Subtotaal huidige pagina</Text>
                            <Text>{formatCurrency(pageSubtotals[0])}</Text>
                        </View>
                    </View>
                )}

                {/* Totals (only on last page) */}
                {pages.length === 1 && totals && (
                    <View style={styles.totals}>
                        <View style={styles.totalRowBegin}>
                            <Text>Subtotaal</Text>
                            <Text>{totals.subtotal}</Text>
                        </View>
                        <View style={styles.totalRow}>
                            <Text>Totaal excl. BTW</Text>
                            <Text>{totals.totalExclBtw}</Text>
                        </View>
                        <View style={styles.totalRow}>
                            <Text>21% BTW</Text>
                            <Text>{totals.btw}</Text>
                        </View>
                        <View style={styles.totalRowFinal}>
                            <Text>Totaal incl. BTW</Text>
                            <Text>{totals.totalInclBtw}</Text>
                        </View>
                    </View>
                )}

                {/* Footer */}
                {footerText && (
                    <View style={styles.footer}>
                        <Text>{footerText}</Text>
                    </View>
                )}
            </Page>

            {/* Continuation Pages */}
            {pages.slice(1).map((pageItems, pageIndex) => (
                <Page size="A4" style={styles.page} key={`continuation-${pageIndex}`}>
                    {/* Logo */}
                    <Image src={companyLogo} style={styles.logo} />

                    {/* Document Details */}
                    <View style={[styles.companyInfo, {flexDirection: 'row', justifyContent: 'space-between', maxWidth: '150px'}]}>
                        <View style={styles.headerCol}>
                            <Text style={styles.headerLabel}>Klantnummer:</Text>
                            <Text style={styles.headerLabel}>Factuurnummer:</Text>
                            <Text style={styles.headerLabel}>Factuurdatum:</Text>
                        </View>
                        <View style={styles.headerCol}>
                            <Text style={styles.headerValue}>{customer.internal_id}</Text>
                            <Text style={styles.headerValue}>{documentNumber}</Text>
                            <Text style={styles.headerValue}>{documentDate}</Text>
                        </View>
                    </View>

                    {/* Products Table */}
                    <View style={[styles.table, styles.continuationHeader]}>
                        <TableHeader />
                        {pageItems.map((item, index) => (
                            <ProductRow key={index} item={item} />
                        ))}
                    </View>

                    {/* Page subtotal if not the last page */}
                    {pageIndex < pages.length - 2 && (
                        <View style={styles.pageSubtotal}>
                            <View style={styles.subtotalRow}>
                                <Text>Subtotaal huidige pagina</Text>
                                <Text>{formatCurrency(pageSubtotals[pageIndex + 1])}</Text>
                            </View>
                        </View>
                    )}

                    {/* Final totals only on last page */}
                    {pageIndex === pages.length - 2 && totals && (
                        <View style={styles.totals}>
                            <View style={styles.totalRowBegin}>
                                <Text>Subtotaal</Text>
                                <Text>{totals.subtotal}</Text>
                            </View>
                            <View style={styles.totalRow}>
                                <Text>Totaal excl. BTW</Text>
                                <Text>{totals.totalExclBtw}</Text>
                            </View>
                            <View style={styles.totalRow}>
                                <Text>21% BTW</Text>
                                <Text>{totals.btw}</Text>
                            </View>
                            <View style={styles.totalRowFinal}>
                                <Text>Totaal incl. BTW</Text>
                                <Text>{totals.totalInclBtw}</Text>
                            </View>
                        </View>
                    )}

                    {/* Footer */}
                    {footerText && (
                        <View style={styles.footer}>
                            <Text>{footerText}</Text>
                        </View>
                    )}
                </Page>
            ))}
        </>
    );
};

export default InvoicePDFPages;